'use client'

// eslint-disable-next-line no-restricted-imports -- the hook value is used on the client side, while workaround is mainly for the server side
import { usePathname } from 'next/navigation'
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
} from 'react'
import { useIsClient } from 'usehooks-ts'

const NextPathnameContext = createContext<string | undefined>(undefined)

type NextPathnameContextProviderProps = PropsWithChildren<{
  /**
   * Server actual pathname value taken from `X-Forwarded-Uri` header
   */
  serverActualPathname: string
}>

/**
 * Rewrites in Next.js set the `usePathname` value on the server following file-based routing.
 * While on the client side the value is equal to the actual pathname user sees in the browser address bar.
 *
 * Use this provider to wrap app in combination with {@link useNextPathname}
 * to provide consistent developer experience.
 *
 * Example:
 * Server result for `usePathname()` - /com/de/nx/login/
 * Client result for `usePathname()` - /nx/login/
 */
export const NextPathnameContextProvider: FC<
  NextPathnameContextProviderProps
> = ({ children, serverActualPathname }) => {
  const pathname = usePathname()
  const isClient = useIsClient()

  /**
   * `usePathname()` on the client is the same user sees in the browser,
   * also it supports Native History API https://nextjs.org/docs/app/getting-started/linking-and-navigating#native-history-api
   */
  const value = isClient ? pathname : serverActualPathname

  return <NextPathnameContext value={value}>{children}</NextPathnameContext>
}

/**
 * Alternative to `usePathname` hook from `next/navigation`.
 * Details in {@link NextPathnameContextProvider}.
 */
export const useNextPathname = (): string => {
  const pathname = useContext(NextPathnameContext)

  if (!pathname) {
    throw new Error(
      'useNextPathname should be wrapped with NextPathnameContextProvider',
    )
  }

  return pathname
}
